import React, { useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useParams } from 'react-router-dom';
import { companiesByIdSelector } from '@/domain/companies/companiesSelector';
import { WS_GRID_PRESET_TYPES } from '@/domain/documents/constants';
import { userGUIDSelector } from '@/domain/env/envSelector';
import {
  gridPresetChosenAction,
  gridPresetSavedAction,
  gridPresetResetAction,
} from '@/domain/documents/gridPreset/gridPresetActions';
import {
  gridPresetCompanyIdSelector,
  gridPresetDefaultColumnsSelector,
  gridPresetIdSelector,
  gridPresetSharedIdSelector,
  gridPresetToSaveSelector,
  isGridPresetChangedSelector,
} from '@/domain/documents/gridPreset/gridPresetSelectors';
import { toPresetConfig } from '@/domain/documents/gridPreset/convert';
import {
  useDeleteGridPresetMutation,
  useGridPresets,
  useSaveGridPresetMutation,
  useSharedGridPreset,
} from '@/domain/documents/queries/gridPresets';
import {
  NO_GRID_PRESETS,
  gridPresetsList,
  toDefaultMenuPreset,
  toMenuPresets,
} from '@/domain/documents/queries/gridPresets.select';
import { savedPresetOf } from '@/domain/documents/queries/gridPresets.merge';
import { useErpConnections } from '@/domain/erpIntegration/queries/connections';
import { companyConnectedErp } from '@/domain/erpIntegration/queries/connections.select';
import { FormattedMessage } from 'react-intl';

import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import Menu from '@mui/material/Menu';
import ListItem from '@mui/material/ListItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  GridPresetsMenuItem,
  GridPresetsMenuToggle,
} from '@/pages/company/WorkspaceRoute/components/CompanyBarPanel/components/GridPresetsMenu/StyledComponents';
import DialogCreateGridPreset from '@/pages/company/WorkspaceRoute/components/CompanyBarPanel/components/GridPresetsMenu/DialogCreateGridPreset';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const LIST_PADDING_TOP = 8;
const MAX_VISIBLE_ITEM_COUNT = 5;

const ItemWrapper = ({ children, getRef }) => {
  const ref = React.useRef(null);

  React.useLayoutEffect(() => {
    getRef(ref.current);
  }, []);

  return <>{React.cloneElement(children, { ref })}</>;
};

const createMailTo = (id) => {
  const { location } = window;
  const link = `${location.origin}${location.pathname}?sharedPreset=${id}`;

  const subject = '';
  return `mailto:?subject=${subject}&body=${link}`;
};

const GridPresets = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const { companyId = '' } = useParams();
  const { data: erpName = null } = useErpConnections(companyId || null, { select: companyConnectedErp });
  const companies = useSelector(companiesByIdSelector);
  const userGUID = useSelector(userGUIDSelector);
  const layoutCompanyId = useSelector(gridPresetCompanyIdSelector);
  const presetId = useSelector(gridPresetIdSelector);
  const sharedPresetId = useSelector(gridPresetSharedIdSelector);
  const layoutToSave = useSelector(gridPresetToSaveSelector);
  const defaultColumns = useSelector(gridPresetDefaultColumnsSelector);
  const isPresetChanges = useSelector(isGridPresetChangedSelector);
  const isResolved = layoutCompanyId === companyId;

  // Both answers are already cached by the grid's layout hook (staleTime: Infinity): no request from here.
  const { data: presets = NO_GRID_PRESETS } = useGridPresets(companyId, { select: gridPresetsList });
  const { data: sharedAnswer } = useSharedGridPreset(companyId, sharedPresetId);
  const { mutate: saveGridPreset } = useSaveGridPresetMutation(companyId);
  const { mutate: deleteGridPreset } = useDeleteGridPresetMutation(companyId);
  // A save or delete answer applies only while the layout is still the company's and preset's it was meant for, read
  // when it lands: after a company switch or another preset was chosen meanwhile, it must not change that layout.
  const isStillOn = useCallback(
    (layoutCompanyIdThen, presetIdThen) => {
      const state = store.getState();
      return gridPresetCompanyIdSelector(state) === layoutCompanyIdThen && gridPresetIdSelector(state) === presetIdThen;
    },
    [store],
  );

  const gridPresets = useMemo(
    () => toMenuPresets({ presets, shared: sharedAnswer?.result ?? null, defaultColumns }),
    [presets, sharedAnswer, defaultColumns],
  );
  const gridCurrentPreset = useMemo(
    () => gridPresets.find((p) => p.id === presetId) || gridPresets[0],
    [gridPresets, presetId],
  );

  const [isNew, setNew] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [heights, setHeights] = useState([]);
  const [stickyHeight, setStickyHeight] = useState('auto');

  const selectedPreset = useMemo(() => gridCurrentPreset, [gridCurrentPreset]);

  const popupState = usePopupState({ variant: 'popover', popupId: 'GridPresetsMenu' });
  const { close: closeMenu, isOpen: isOpenMenu } = popupState;

  const onOpenModalAdd = useCallback(() => {
    setNew(true);
    setOpenModal(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  // Choosing the current preset again drops its changes, like Reset; without changes there is nothing to apply.
  const handleMenuItemClick = useCallback(
    (preset) => () => {
      if (preset.id !== presetId || isPresetChanges) {
        dispatch(gridPresetChosenAction({ presetId: preset.id, saved: preset.layout }));
      }
      closeMenu();
    },
    [dispatch, closeMenu, presetId, isPresetChanges],
  );

  const handleAddMenuItemClick = () => {
    closeMenu();
    onOpenModalAdd();
  };

  // Deleting the current preset makes Default current, without its changes.
  const deletePreset = useCallback(
    (id) => (e) => {
      e.stopPropagation();
      const requestCompanyId = companyId;
      deleteGridPreset(
        { query: { type: 'grid' }, body: { id } },
        {
          onSuccess: () => {
            if (isStillOn(requestCompanyId, id)) {
              const fallback = toDefaultMenuPreset(defaultColumns);
              dispatch(gridPresetChosenAction({ presetId: fallback.id, saved: fallback.layout }));
            }
          },
        },
      );
    },
    [deleteGridPreset, companyId, isStillOn, defaultColumns, dispatch],
  );

  const isActiveSave = useMemo(
    () => selectedPreset.type === WS_GRID_PRESET_TYPES.custom && isPresetChanges,
    [selectedPreset, isPresetChanges],
  );

  const onClearFilterChanges = useCallback(() => {
    dispatch(gridPresetResetAction());
  }, [dispatch]);

  // The working layout becomes the saved one, and the saved preset current — without applying anything to the grid,
  // which already shows it. The current preset's settings for columns this grid lacks go along (`gridPresetToSaveSelector`).
  const onSave = useCallback(
    ({ name, isAll, id = null }) => {
      if (!layoutToSave) return;
      const body = {
        id,
        name,
        user_id: userGUID,
        ...(isAll ? {} : { company_id: companyId }),
        config: toPresetConfig(layoutToSave),
      };
      const requestCompanyId = companyId;
      const requestPresetId = presetId;
      saveGridPreset(
        { query: { type: 'grid', version: 'be_driven_grid' }, body },
        {
          onSuccess: (answer) => {
            const saved = savedPresetOf(answer.result, { id, name }, presets);
            if (saved && isStillOn(requestCompanyId, requestPresetId)) {
              dispatch(gridPresetSavedAction({ presetId: saved.id, saved: layoutToSave }));
            }
          },
        },
      );
      setOpenModal(false);
    },
    [layoutToSave, userGUID, companyId, presetId, saveGridPreset, isStillOn, presets, dispatch],
  );

  const onSaveCurrentPreset = useCallback(() => {
    const { name, isAll, id } = selectedPreset;
    onSave({
      id,
      isNew: false,
      name,
      isAll,
    });
  }, [selectedPreset, onSave]);

  const getHeightUpdater = (name, id) => (ref) =>
    setHeights((h) => [...h, { id, height: ref?.offsetHeight || 'auto' }]);

  const maxHeight =
    typeof heights[0]?.height === 'string'
      ? 'auto'
      : heights.slice(0, MAX_VISIBLE_ITEM_COUNT).reduce((a, v) => a + v.height, 0) + stickyHeight + LIST_PADDING_TOP;

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <GridPresetsMenuToggle
          variant="outlined"
          size="small"
          presetType={selectedPreset.type}
          {...bindTrigger(popupState)}
        >
          <Typography variant="body2" noWrap>
            <FormattedMessage id={`document.bar.preset.${selectedPreset.type}`} defaultMessage={selectedPreset.name} />
            {selectedPreset.isAll && <FormattedMessage id="document.bar.preset.all" defaultMessage="(all)" />}
          </Typography>
          {isOpenMenu ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </GridPresetsMenuToggle>
        <Menu
          {...bindMenu(popupState)}
          slotProps={{
            list: { sx: { pb: 0 } },
            paper: {
              sx: {
                minWidth: '25ch',
                maxHeight,
              },
            },
          }}
        >
          {gridPresets.map((item) => (
            <ItemWrapper
              key={`${item.id}${item.type}`}
              getRef={getHeightUpdater(WS_GRID_PRESET_TYPES.default ? 'defaultItem' : 'item')}
            >
              <GridPresetsMenuItem
                selected={item.id === selectedPreset.id}
                onClick={handleMenuItemClick(item)}
                presetType={item.type}
              >
                <Box display="flex" alignItems="center" pr={2}>
                  <Typography variant="body2">
                    <FormattedMessage id={`document.bar.preset.${item.type}`} defaultMessage={item.name} />
                    {item.isAll && <FormattedMessage id="document.bar.preset.all" defaultMessage="(all)" />}
                  </Typography>
                </Box>

                {item.type === WS_GRID_PRESET_TYPES.custom && (
                  <Stack direction="row" spacing={1} ml="auto">
                    <IconButton
                      size="small"
                      color="primary"
                      component={Link}
                      href={createMailTo(item.id)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <LinkIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={deletePreset(item.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </GridPresetsMenuItem>
            </ItemWrapper>
          ))}
          <ItemWrapper getRef={(ref) => setStickyHeight(ref.offsetHeight || 'auto')}>
            <ListItem sx={{ backgroundColor: 'common.white', position: 'sticky', bottom: 0 }}>
              <Button onClick={handleAddMenuItemClick} color="primary" size="small" fullWidth>
                <FormattedMessage id="button.add" defaultMessage="Add" />
              </Button>
            </ListItem>
          </ItemWrapper>
        </Menu>

        {gridCurrentPreset.type === WS_GRID_PRESET_TYPES.custom ? (
          isActiveSave && (
            <Button size="small" onClick={onSaveCurrentPreset}>
              <FormattedMessage id="button.save" defaultMessage="Save" />
            </Button>
          )
        ) : (
          <Button size="small" onClick={onOpenModalAdd}>
            <FormattedMessage id="button.add" defaultMessage="Add" />
          </Button>
        )}

        {isPresetChanges && isResolved && (
          <Button variant="outlined" size="small" onClick={onClearFilterChanges} disabled={!isPresetChanges}>
            <FormattedMessage id="button.reset" defaultMessage="Reset" />
          </Button>
        )}
      </Stack>

      {isOpenModal && (
        <DialogCreateGridPreset
          isNew={isNew}
          preset={selectedPreset}
          onClose={onCloseModal}
          onSave={onSave}
          gridPresets={gridPresets}
          erpName={erpName}
          isMultiCompany={companies.size > 1}
        />
      )}
    </>
  );
};

export default GridPresets;
